import { Timestamp } from '@src/common/time/Timestamp';
import type { Order } from '@src/orders/domain/Order';
import type { PermitApplication } from '@src/orders/domain/PermitApplication';
import {
  CompanyPhysicalAddressDetailsField,
  CompanyPhysicalAddressField,
  CompanyPhysicalCityField,
  CompanyPhysicalStateField,
  CompanyPhysicalZipCodeField,
} from '@src/permitField/CompanyAddressFields';
import {
  CarrierContactNameField,
  CompanyEmailField,
  CompanyFaxField,
  CompanyNameField,
  CompanyPhoneField,
  DoingBusinessAsField,
} from '@src/permitField/CompanyInformationSectionFields';
import {
  DriverEmailField,
  DriverLicenseNumberField,
  DriverNameField,
  DriverPhoneField,
} from '@src/permitField/DriverInformationFields';
import { usePermitViewFieldLabels } from '@src/permitField/hooks/usePermitViewFieldLabels';
import {
  PermitContactEmailAddressField,
  PermitContactNameField,
  PermitContactPhoneField,
  PrimaryDeliveryTypeField,
  PrimaryDeliveryTypeValueField,
  SecondaryDeliveryTypeField,
  SecondaryDeliveryTypeValueField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import { PONumberField } from '@src/permitField/PONumberField';
import { CategorySection } from '@src/permits/CategorySection';
import { FieldRow } from '@src/permits/PermitSection';
import { useTranslation } from 'react-i18next';

type DeliveryTypeRowProps = {
  typeFieldName: string;
  valueFieldName: string;
  permitApplication: PermitApplication;
  order: Order;
};
const DeliveryTypeRow = ({
  typeFieldName,
  valueFieldName,
  permitApplication,
  order,
}: DeliveryTypeRowProps) => {
  const { formatField } = usePermitViewFieldLabels({ permitApplication });

  const typeValue = permitApplication.getFieldValue(typeFieldName)?.toString();
  const value = permitApplication.getFieldValue(valueFieldName)?.toString();

  const lastUpdatedDate = order.getLastUpdatedFor(valueFieldName);
  const lastUpdated = lastUpdatedDate
    ? new Timestamp(lastUpdatedDate).formatUTCDate()
    : undefined;
  const { fieldLabel: typeLabel } = formatField({
    field: typeFieldName,
    value: typeValue,
  });

  return (
    <>
      <FieldRow
        id={`${typeFieldName}-row`}
        label={typeLabel}
        value={undefined}
      />
      {typeValue && value && (
        <FieldRow
          id={`${typeFieldName}-value-row`}
          label={typeValue}
          value={value}
          lastUpdated={lastUpdated}
        />
      )}
    </>
  );
};
export const CarrierAndPermitDeliveryCategory = ({
  permitApplication,
  order,
}: {
  permitApplication: PermitApplication;
  order: Order;
}) => {
  const { t } = useTranslation();
  const fields = permitApplication.getFieldsBySection(
    'carrierAndPermitDelivery',
  );
  if (fields.size === 0) return null;

  return (
    <CategorySection
      title={t('orderSections.carrierAndPermitDelivery')}
      requiredFields={Array.from(fields.values()).flat()}
      permitApplication={permitApplication}
      order={order}
      subsections={[
        {
          label: t('companyInformation'),
          fields: [
            CompanyNameField.fieldName,
            DoingBusinessAsField.fieldName,
            CompanyPhoneField.fieldName,
            CompanyFaxField.fieldName,
            CarrierContactNameField.fieldName,
            CompanyEmailField.fieldName,
          ],
        },
        {
          label: t('companyAddress.title'),
          fields: [
            CompanyPhysicalAddressField.fieldName,
            CompanyPhysicalAddressDetailsField.fieldName,
            CompanyPhysicalCityField.fieldName,
            CompanyPhysicalStateField.fieldName,
            CompanyPhysicalZipCodeField.fieldName,
          ],
        },
        {
          label: t('PONumber'),
          fields: [PONumberField.fieldName],
        },
        {
          label: t('driverInformation'),
          fields: [
            DriverNameField.fieldName,
            DriverEmailField.fieldName,
            DriverPhoneField.fieldName,
            DriverLicenseNumberField.fieldName,
          ],
        },
        {
          label: t('orderInformation'),
          fields: [
            PermitContactNameField.fieldName,
            PermitContactPhoneField.fieldName,
            PermitContactEmailAddressField.fieldName,
          ],
        },
        {
          label: t('permitDeliveryAndNotifications'),
          renderFields: () => (
            <>
              <DeliveryTypeRow
                typeFieldName={PrimaryDeliveryTypeField.fieldName}
                valueFieldName={PrimaryDeliveryTypeValueField.fieldName}
                permitApplication={permitApplication}
                order={order}
              />
              <DeliveryTypeRow
                typeFieldName={SecondaryDeliveryTypeField.fieldName}
                valueFieldName={SecondaryDeliveryTypeValueField.fieldName}
                permitApplication={permitApplication}
                order={order}
              />
            </>
          ),
        },
      ]}
    />
  );
};
